import { TQuote } from 'src/types/props.types';
import { eventLog, TTickEvent } from './EventLog';

/**
 * EventStream: A unified abstraction over historical and live tick/candle data.
 * Provides slice/filter operations and iterator utilities for replay/playback.
 */

export type TEventStreamFilter = {
    symbol?: string;
    granularity?: number;
    minEpoch?: number;
    maxEpoch?: number;
    source?: 'live' | 'history' | 'both';
};

export type TEventIteratorOptions = {
    startEpoch?: number;
    endEpoch?: number;
    reverse?: boolean; // For stepping backwards
};

class EventStream {
    /**
     * Get a chronological slice of events with optional filtering.
     */
    slice(filter?: TEventStreamFilter): TTickEvent[] {
        const minEpoch = filter?.minEpoch ?? -Infinity;
        const maxEpoch = filter?.maxEpoch ?? Infinity;
        
        // Use EventLog's optimized range query
        const rangeEvents = eventLog.getRange(minEpoch, maxEpoch);
        
        if (!filter) return rangeEvents;

        return rangeEvents.filter(event => {
            if (filter.symbol && event.symbol !== filter.symbol) return false;
            if (filter.granularity !== undefined && event.granularity !== filter.granularity) return false;
            if (filter.source && filter.source !== 'both' && event.source !== filter.source) return false;
            return true;
        });
    }

    /**
     * Get all events (no filtering).
     */
    getAll(): TTickEvent[] {
        return this.slice();
    }

    /**
     * Get events within a time window.
     */
    getTimeWindow(startEpoch: number, endEpoch: number): TTickEvent[] {
        return eventLog.getRange(startEpoch, endEpoch);
    }

    /**
     * Get the latest N events.
     */
    getLatest(count: number): TTickEvent[] {
        const all = this.slice();
        return all.slice(-count);
    }

    /**
     * Find the closest event to a given epoch.
     */
    findClosest(targetEpoch: number): TTickEvent | undefined {
        const all = this.slice();
        if (all.length === 0) return undefined;

        // Binary search for efficiency
        let left = 0;
        let right = all.length - 1;
        let closest = all[0];
        let minDist = Math.abs(all[0].epoch - targetEpoch);

        while (left <= right) {
            const mid = Math.floor((left + right) / 2);
            const dist = Math.abs(all[mid].epoch - targetEpoch);

            if (dist < minDist) {
                minDist = dist;
                closest = all[mid];
            }

            if (all[mid].epoch < targetEpoch) {
                left = mid + 1;
            } else if (all[mid].epoch > targetEpoch) {
                right = mid - 1;
            } else {
                return all[mid]; // Exact match
            }
        }

        return closest;
    }

    /**
     * Create an iterator for stepping through events.
     */
    *iterator(options?: TEventIteratorOptions): Generator<TTickEvent, void, unknown> {
        const events = this.slice({
            minEpoch: options?.startEpoch,
            maxEpoch: options?.endEpoch,
        });

        if (options?.reverse) {
            for (let i = events.length - 1; i >= 0; i--) {
                yield events[i];
            }
        } else {
            for (const event of events) {
                yield event;
            }
        }
    }

    /**
     * Get statistics about the event stream.
     */
    getStats(filter?: TEventStreamFilter) {
        const events = this.slice(filter);
        
        if (events.length === 0) {
            return {
                count: 0,
                firstEpoch: null,
                lastEpoch: null,
                timeSpan: 0,
                sources: { live: 0, history: 0 },
            };
        }

        const sources = { live: 0, history: 0 };
        events.forEach(e => sources[e.source]++);

        return {
            count: events.length,
            firstEpoch: events[0].epoch,
            lastEpoch: events[events.length - 1].epoch,
            timeSpan: events[events.length - 1].epoch - events[0].epoch,
            sources,
        };
    }

    /**
     * Check if events exist in a time range.
     */
    hasEventsInRange(startEpoch: number, endEpoch: number): boolean {
        return this.getTimeWindow(startEpoch, endEpoch).length > 0;
    }

    /**
     * Get the first and last event epochs (bounds of available data).
     * OPTIMIZATION: Call eventLog.getBounds() directly instead of slice() to avoid full table scan.
     */
    getBounds(): { first: number | null; last: number | null } {
        return eventLog.getBounds();
    }

    /**
     * Sample events at regular intervals (for downsampling/visualization).
     */
    sample(targetCount: number, filter?: TEventStreamFilter): TTickEvent[] {
        const events = this.slice(filter);
        
        if (events.length <= targetCount) return events;

        const step = Math.floor(events.length / targetCount);
        const sampled: TTickEvent[] = [];

        for (let i = 0; i < events.length; i += step) {
            sampled.push(events[i]);
        }

        // Always include the last event
        if (sampled[sampled.length - 1] !== events[events.length - 1]) {
            sampled.push(events[events.length - 1]);
        }

        return sampled;
    }

    /**
     * Convert events back to TQuote format for compatibility.
     */
    toQuotes(events: TTickEvent[]): TQuote[] {
        return events.map(e => e.raw);
    }
}

export const eventStream = new EventStream();

// Global exposure for debugging
declare global {
    interface Window {
        __eventStream?: EventStream;
    }
}

if (typeof window !== 'undefined') {
    window.__eventStream = eventStream as any;
}
